import { flushSync } from 'react-dom';
import copyTextToClipboard from 'react-utils/copyTextToClipboard';
import { getCellExternalId } from '../../table/utils/cellIdUtils';
import { serializeSelectionToTsv } from './serializeSelectionToTsv';
/**
 * Serializes the selected cells to TSV (the format spreadsheet apps expect on
 * paste) and writes them to the clipboard. Shared by the Cmd/Ctrl+C keyboard
 * handler (useCopyCellSelection) and the floating action menu's Copy button so
 * both paths run identical code. No-op for an empty selection.
 *
 * For selections that include virtualized (off-screen) cells whose DOM nodes
 * are absent, it synchronously renders only those missing cells into a hidden
 * staging area via flushSync before scraping, then releases the staging render
 * immediately after the clipboard write is initiated. Both callers invoke this
 * within an active user activation (keydown / menu-button click), so the
 * flushSync + clipboard write stay within a synchronous user gesture.
 */
export const copyCellSelection = ({
  coords,
  columns,
  rows,
  getRowId,
  getCellCopyValue,
  setStagingEntries,
  stagingContainerRef
}) => {
  if (coords.length === 0) return;

  // Identify coords whose cells are absent from the live DOM (virtualized away).
  const missingCoords = coords.filter(({
    rowIndex,
    columnIndex
  }) => {
    const row = rows[rowIndex];
    const column = columns[columnIndex];
    if (row === undefined || column === undefined) return false;
    const externalId = getCellExternalId(column.columnNamespace, column.columnName, getRowId(row));
    return !document.getElementById(externalId);
  });

  // For missing cells only: synchronously render them into the hidden staging
  // area so scraping can proceed without async gaps (keeping the user
  // activation live for the clipboard write).
  let stagingContainer = null;
  if (missingCoords.length > 0) {
    const newEntries = missingCoords.flatMap(({
      rowIndex,
      columnIndex
    }) => {
      const row = rows[rowIndex];
      const column = columns[columnIndex];
      if (row === undefined || column === undefined) return [];
      return [{
        rowIndex,
        columnIndex,
        row,
        column
      }];
    });
    // flushSync commits the staging render synchronously (render + layout
    // effects, and in React 18 pending passive/useEffect callbacks are flushed
    // before it returns) so we can scrape in the same tick as the gesture,
    // keeping the user activation live for the clipboard write.
    //
    // Accepted gap: a CellComponent whose text resolves ASYNCHRONOUSLY (a fetch
    // / promise / rAF started in an effect that sets state on resolution)
    // settles on a later tick, after we've already scraped — that cell scrapes
    // as whatever rendered synchronously. Synchronous effect-driven text is
    // captured; only async-resolved content is not. We accept this because (a)
    // awaiting settlement would break the synchronous-gesture guarantee the
    // clipboard write depends on, and (b) our production consumers render
    // display TEXT synchronously (labels, formatted properties, sensitivity
    // mask); the async-driven parts are non-text (icons, avatars), which are
    // out of scope for copy.
    flushSync(() => setStagingEntries(newEntries));
    stagingContainer = stagingContainerRef.current;
  }
  const tsv = serializeSelectionToTsv(coords, columns, rows, getRowId, getCellCopyValue, stagingContainer);
  void copyTextToClipboard(tsv);

  // Release staging after the clipboard write is initiated; React will batch
  // this with any concurrent updates.
  setStagingEntries([]);
};